import { Input, InputGroup, InputProps, useMergeRefs, useStyleConfig } from '@chakra-ui/react';
import { FocusEvent, forwardRef, ReactNode, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { useFieldValidationStates } from '../hooks/useFieldValidationStates';
import { SimpleField, SimpleXProps } from './SimpleField';
import { useSimpleFormContext } from './SimpleForm';

type SimpleInputProps = SimpleXProps &
  InputProps & {
    selectOnFocus?: boolean;
    isHighlighted?: boolean;
    inputAttrs?: InputProps;
    noOfDecimals?: number;
    formatDisplayValue?: (value: any) => string;
    leftElement?: ReactNode;
    rightElement?: ReactNode;
  };

export const SimpleInput = forwardRef((props: SimpleInputProps, ref) => {
  const {
    name,
    label,
    helper,
    placeholder,
    size = 'md',
    type = 'text',
    ignoreOnBlur,
    hideError,
    formatDisplayValue,
    selectOnFocus = false,
    pattern,
    isHighlighted = false,
    leftElement,
    rightElement,
    inputAttrs = {}, // Anything you want to pass to the input itself, for mainly text styling
    ...rest
  } = props;

  const { isInvalid: hasError } = useFieldValidationStates(name as string);
  const isInvalid = !hideError && hasError;
  const { simpleOnBlur } = useSimpleFormContext();
  const { register, watch, setValue } = useFormContext();

  const formProps = register(name, {
    setValueAs: (value) => {
      if (value === '') return null;
      return value;
    },
  });
  const mergedRef = useMergeRefs(formProps.ref, ref);
  const value = watch(name);

  const onBlur = (event: any) => {
    if (ignoreOnBlur) return;

    const incomingEvent = {
      ...event,
      target: { ...event.target, name, value },
    };

    formProps.onBlur(incomingEvent);
    simpleOnBlur?.(incomingEvent);
  };

  const onFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (selectOnFocus) event.target?.focus();
  };

  const borderColor = isInvalid ? 'error' : isHighlighted ? 'orange.500' : 'transparent';
  const highlightWarning = isHighlighted ? 'This triggered a threshold! ' : '';
  const finalHelper = `${highlightWarning}${helper || ''}`;

  const displayValue = useMemo(() => {
    let display = formatDisplayValue?.(value);

    const isCheckbox = type === 'checkbox';
    if (!display && isCheckbox) display = value ? 'Yes' : 'No';

    if (!display && Array.isArray(value)) display = value?.join(', ');

    if (!display) display = value;
    if (!display) display = '';

    return display;
  }, [formatDisplayValue, value, type]);

  const defaultHover = useStyleConfig('Input', { variant: 'filled' });

  if (!formatDisplayValue) {
    return (
      <SimpleField<InputProps> name={name} label={label} helper={finalHelper} hideError={hideError} {...rest}>
        <InputGroup>
          {leftElement}
          <Input
            type={type}
            variant="filled"
            placeholder={placeholder}
            size={size}
            pattern={pattern}
            {...formProps}
            onBlur={onBlur}
            onFocus={onFocus}
            px={2}
            borderColor={borderColor}
            isDisabled={rest.isDisabled}
            {...inputAttrs}
            autoComplete="off"
            _groupHover={defaultHover?.['field']?._hover}
            ref={mergedRef}
          />
          {rightElement}
        </InputGroup>
      </SimpleField>
    );
  }

  const onChange = (event: any) => {
    // Remove any non-digits from value
    const parsedValue = event.target.value.replace(/\D/g, '');
    setValue(name, parsedValue);
  };

  return (
    <SimpleField<InputProps> name={name} label={label} helper={helper} hideError={hideError} {...rest}>
      <Input
        variant="filled"
        placeholder={placeholder}
        pattern={pattern}
        onBlur={onBlur}
        onFocus={onFocus}
        px={2}
        borderColor={borderColor}
        isDisabled={rest.isDisabled}
        {...inputAttrs}
        autoComplete="off"
        value={displayValue}
        onChange={onChange}
        isInvalid={isInvalid}
        ref={mergedRef}
      />
      {/*<Input
        type={type}
        size={size}
        {...formProps}
        autoComplete="off"
        isDisabled={rest.isDisabled}
        display="none"
        value={value}
      />*/}
    </SimpleField>
  );
});
